// This code has been fully generated by an AI.
import { useEffect, useMemo, useState } from 'react';

interface ApiOperation {
  tags?: string[];
  summary?: string;
  description?: string;
  operationId?: string;
  parameters?: Array<{ name: string; in: string; required?: boolean; description?: string; schema?: unknown }>;
  requestBody?: { required?: boolean; description?: string; content?: Record<string, { schema?: unknown }> };
  responses?: Record<string, {
    description?: string;
    content?: Record<string, { schema?: unknown }>;
  }>;
}

interface ApiPathItem extends Partial<Record<typeof METHODS[number], ApiOperation>> {
  parameters?: ApiOperation['parameters'];
}

interface ApiSpec {
  info?: { title?: string; version?: string; description?: string };
  servers?: Array<{ url: string; description?: string }>;
  paths?: Record<string, ApiPathItem>;
}

const METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

export default function ApiDocsPage() {
  const [spec, setSpec] = useState<ApiSpec | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch('/openapi.json')
      .then(async (response) => {
        if (!response.ok) throw new Error(`Could not load OpenAPI spec (HTTP ${response.status}).`);
        return response.json() as Promise<ApiSpec>;
      })
      .then((document) => { if (!cancelled) setSpec(document); })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load OpenAPI spec.');
      });
    return () => { cancelled = true; };
  }, []);

  const groups = useMemo(() => {
    const grouped = new Map<string, Array<{
      path: string;
      method: typeof METHODS[number];
      operation: ApiOperation;
      parameters: NonNullable<ApiOperation['parameters']>;
    }>>();
    for (const [path, pathItem] of Object.entries(spec?.paths ?? {})) {
      for (const method of METHODS) {
        const operation = pathItem[method];
        if (!operation) continue;
        const term = search.trim().toLowerCase();
        const searchable = `${path} ${operation.summary ?? ''} ${operation.description ?? ''} ${(operation.tags ?? []).join(' ')}`.toLowerCase();
        if (term && !searchable.includes(term)) continue;
        const tag = operation.tags?.[0] ?? 'Other';
        const entries = grouped.get(tag) ?? [];
        entries.push({
          path,
          method,
          operation,
          parameters: [...(pathItem.parameters ?? []), ...(operation.parameters ?? [])],
        });
        grouped.set(tag, entries);
      }
    }
    return [...grouped.entries()].sort(([left], [right]) => left.localeCompare(right));
  }, [spec, search]);

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">{spec?.info?.title ?? 'API Explorer'}</h1>
          <p className="muted text-sm m-0">Explore the registry API{spec?.info?.version ? ` · v${spec.info.version}` : ''}</p>
        </div>
        <a className="btn btn--secondary" href="/openapi.json" target="_blank" rel="noopener noreferrer">Open raw OpenAPI</a>
      </div>
      <div className="page__body api-explorer">
        {spec?.info?.description && <p className="api-explorer__intro">{spec.info.description}</p>}
        {spec?.servers && (
          <div className="api-explorer__servers" aria-label="API servers">
            {spec.servers.map((server) => (
              <span key={server.url}><strong>{server.description ?? 'Server'}</strong><code>{server.url}</code></span>
            ))}
          </div>
        )}
        <div className="search-bar mb-2">
          <input
            className="search-input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter by endpoint, tag, or description…"
            aria-label="Filter API operations"
          />
          <span className="pill pill--accent pill--roomy">
            {groups.reduce((count, [, operations]) => count + operations.length, 0)} operations
          </span>
        </div>
        {error && <div className="alert alert--error" role="alert">{error}</div>}
        {!spec && !error && <div className="centered-status" role="status"><span className="spinner" aria-hidden="true" /> Loading API description…</div>}
        {spec && groups.length === 0 && <div className="empty-state"><h2>No operations match</h2><p>Try a different search term.</p></div>}
        {groups.map(([tag, operations]) => (
          <section className="api-explorer__group" key={tag}>
            <div className="api-explorer__group-heading">
              <h2>{tag}</h2>
              <span className="muted text-xs">{operations.length} endpoint{operations.length === 1 ? '' : 's'}</span>
            </div>
            {operations.map(({ path, method, operation, parameters }) => (
              <details className={`api-operation api-operation--${method}`} key={`${method}:${path}`}>
                <summary>
                  <span className={`api-operation__method api-operation__method--${method}`}>{method.toUpperCase()}</span>
                  <code className="api-operation__path">{path}</code>
                  <span className="api-operation__summary">{operation.summary ?? operation.operationId ?? 'Endpoint'}</span>
                  <span className="api-operation__chevron" aria-hidden="true">⌄</span>
                </summary>
                <div className="api-operation__details">
                  {operation.description && <p>{operation.description}</p>}
                  {parameters.length > 0 && (
                    <section>
                      <h3>Parameters</h3>
                      <ul className="api-operation__list">
                        {parameters.map((parameter) => (
                          <li key={`${parameter.in}:${parameter.name}`}>
                            <code>{parameter.name}</code> <span className="muted">{parameter.in}{parameter.required ? ' · required' : ''}</span>
                            {parameter.description && <p>{parameter.description}</p>}
                            {parameter.schema !== undefined && (
                              <pre className="api-operation__schema">{JSON.stringify(parameter.schema, null, 2)}</pre>
                            )}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {operation.requestBody && (
                    <section>
                      <h3>Request body{operation.requestBody.required ? ' · required' : ''}</h3>
                      {operation.requestBody.description && <p>{operation.requestBody.description}</p>}
                      {Object.entries(operation.requestBody.content ?? {}).map(([type, content]) => (
                        <div className="api-operation__schema-content" key={type}>
                          <span className="pill pill--accent">{type}</span>
                          {content.schema !== undefined && (
                            <pre className="api-operation__schema">{JSON.stringify(content.schema, null, 2)}</pre>
                          )}
                        </div>
                      ))}
                    </section>
                  )}
                  {operation.responses && (
                    <section>
                      <h3>Responses</h3>
                      <ul className="api-operation__responses">
                        {Object.entries(operation.responses).map(([status, response]) => (
                          <li key={status}>
                            <code>{status}</code>
                            <span>{response.description ?? 'Response'}</span>
                            {Object.entries(response.content ?? {}).map(([type, content]) => (
                              <div className="api-operation__schema-content" key={type}>
                                <span className="pill pill--accent">{type}</span>
                                {content.schema !== undefined && (
                                  <pre className="api-operation__schema">{JSON.stringify(content.schema, null, 2)}</pre>
                                )}
                              </div>
                            ))}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>
              </details>
            ))}
          </section>
        ))}
      </div>
    </>
  );
}
